/**
 * @fileoverview Count-up animation: elements with [vdx-count-up] animate from 0 to their text content when they enter the viewport.
 */

import { logWarn, showErrorOnElement } from './errors.js';

const MODULE = 'countup';

const SELECTOR = '[vdx-count-up]';
const ATTR_DURATION = 'vdx-count-up';
const ATTR_EASE = 'vdx-count-ease';
const DEFAULT_DURATION_MS = 2000;
const DEFAULT_EASE = 'linear';

/** Attribute set when an element has been passed to the observer to avoid duplicate observation. */
const WIRED_ATTR = 'vdx-wired';

/** Easing functions: t in [0, 1], returns eased progress. */
const EASING = {
    linear: (t) => t,
    easeInQuad: (t) => t * t,
    easeOutQuad: (t) => t * (2 - t),
    easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
    easeInCubic: (t) => t * t * t,
    easeOutCubic: (t) => (--t) * t * t + 1,
    easeInOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),
    easeInQuart: (t) => t * t * t * t,
    easeOutQuart: (t) => 1 - (--t) * t * t * t,
    easeInOutQuart: (t) => (t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t),
    easeInQuint: (t) => t * t * t * t * t,
    easeOutQuint: (t) => 1 + (--t) * t * t * t * t,
    easeInOutQuint: (t) => (t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * (--t) * t * t * t * t),
    easeOutExpo: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
};

/**
 * Parses the target number from the element's text content.
 * @param {Element} element
 * @returns {{ value: number, valid: boolean }}
 */
function parseTarget(element) {
    const raw = element.textContent != null ? String(element.textContent).trim() : '';
    const value = Number(raw);
    return { value: Number.isNaN(value) ? 0 : Math.max(0, Math.floor(value)), valid: !Number.isNaN(value) && raw !== '' };
}

/**
 * Runs the count-up animation on the element (0 → target over duration with easing).
 * @param {Element} element - Element with [vdx-count-up]; its textContent is the target number.
 */
function startCount(element) {
    if (!element || typeof element.textContent === 'undefined') return;
    const { value: targetValue, valid } = parseTarget(element);
    if (!valid || targetValue < 0) {
        const msg = `vdx-count-up target must be a non-negative number in the element's text (e.g. "100"). Got: "${element.textContent ?? ''}".`;
        logWarn(msg, { module: MODULE, context: { textContent: element.textContent } });
        showErrorOnElement(element, msg);
        return;
    }

    const duration = Math.max(0, Number(element.getAttribute(ATTR_DURATION)) || DEFAULT_DURATION_MS);
    const easeName = (element.getAttribute(ATTR_EASE) || DEFAULT_EASE).trim().toLowerCase();
    const ease = EASING[easeName] || EASING[DEFAULT_EASE];

    let startTime = null;
    function animate(timestamp) {
        if (startTime == null) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const linearProgress = Math.min(elapsed / duration, 1);
        const easedProgress = ease(linearProgress);
        const current = Math.floor(easedProgress * targetValue);
        element.textContent = String(current);

        if (linearProgress < 1) {
            requestAnimationFrame(animate);
        } else {
            element.textContent = String(targetValue);
        }
    }
    requestAnimationFrame(animate);
}

/**
 * Initializes count-up: observes [vdx-count-up] elements and animates from 0 to their text value when they enter the viewport.
 * Safe to call multiple times (elements already observed are skipped).
 */
export function countUp() {
    if (typeof document === 'undefined' || typeof IntersectionObserver === 'undefined') return;
    const elements = document.querySelectorAll(SELECTOR);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            observer.unobserve(el);
            startCount(el);
        });
    });

    elements.forEach((el) => {
        if (el.hasAttribute(WIRED_ATTR)) return;
        el.setAttribute(WIRED_ATTR, '');
        observer.observe(el);
    });
}
